import { useEffect, useState } from "react";
import { X, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/auth";

/**
 * Modal that lets the admin review (and amend) a welcome email before it
 * is sent. Loads the default subject + body for the given role/id from the
 * backend, then POSTs to /admin/welcome-email/send with the (possibly
 * edited) values on submit.
 *
 * Props:
 *   target: { role: "vendor"|"couple", id: string, label?: string } | null
 *   onClose: () => void
 */
export const WelcomeEmailModal = ({ target, onClose, onSent }) => {
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [recipient, setRecipient] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");

  useEffect(() => {
    if (!target?.role || !target?.id) return;
    let cancelled = false;
    setLoading(true);
    setRecipient("");
    setRecipientName("");
    setSubject("");
    setBody("");
    api
      .get("/admin/welcome-email/preview", {
        params: { role: target.role, id: target.id },
      })
      .then(({ data }) => {
        if (cancelled) return;
        setRecipient(data.recipient_email || "");
        setRecipientName(data.recipient_name || "");
        setSubject(data.subject || "");
        setBody(data.body || "");
      })
      .catch((err) => {
        toast(
          err?.response?.data?.detail || "Could not load welcome email",
          { duration: 2600 }
        );
        onClose?.();
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [target?.role, target?.id, onClose]);

  if (!target) return null;

  const send = async () => {
    if (!subject.trim() || !body.trim()) {
      toast("Subject and body required", { duration: 2400 });
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/admin/welcome-email/send", {
        role: target.role,
        id: target.id,
        subject,
        body,
      });
      toast(
        data?.sent
          ? `Welcome email sent to ${data.recipient}`
          : `Logged — email service unavailable (${data?.recipient || recipient})`,
        { duration: 3200 }
      );
      onSent?.();
      onClose?.();
    } catch (err) {
      toast(
        err?.response?.data?.detail || "Couldn't send the welcome email",
        { duration: 2800 }
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      role="dialog"
      data-testid="welcome-email-modal"
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
    >
      <div
        className="absolute inset-0 bg-[var(--ld-ink)]/55"
        onClick={onClose}
        aria-hidden
      />
      <div className="relative w-full max-w-2xl bg-[var(--ld-ivory)] shadow-2xl overflow-hidden">
        <div className="flex items-start justify-between gap-4 px-7 py-5 border-b border-[var(--ld-border)]/70">
          <div>
            <span className="font-accent text-[10px] tracking-[0.28em] uppercase text-[var(--ld-gold-deep)]">
              Welcome email · review before sending
            </span>
            <h3 className="font-serif-display text-2xl mt-1 text-[var(--ld-ink)]">
              {recipientName || (target.role === "vendor" ? "Vendor" : "Couple")}
            </h3>
            <p className="text-[12px] text-[var(--ld-ink-muted)] mt-1">
              To: {recipient || "…"}
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="text-[var(--ld-ink-muted)] hover:text-[var(--ld-ink)] p-1"
          >
            <X size={18} strokeWidth={1.6} />
          </button>
        </div>

        {loading ? (
          <div className="px-7 py-14 flex items-center justify-center text-[var(--ld-ink-muted)]">
            <Loader2 size={18} className="animate-spin" />
            <span className="ml-2 text-sm">Preparing…</span>
          </div>
        ) : (
          <div className="px-7 py-5 space-y-4 max-h-[70vh] overflow-y-auto">
            <label className="block">
              <span className="font-accent text-[10px] tracking-[0.22em] uppercase text-[var(--ld-ink-muted)]">
                Subject
              </span>
              <input
                data-testid="welcome-email-subject"
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="mt-1 w-full h-11 bg-transparent border border-[var(--ld-ink)]/25 px-3 text-sm text-[var(--ld-ink)] focus:outline-none focus:border-[var(--ld-gold-deep)] transition-colors"
              />
            </label>
            <label className="block">
              <span className="font-accent text-[10px] tracking-[0.22em] uppercase text-[var(--ld-ink-muted)]">
                Body
              </span>
              <textarea
                data-testid="welcome-email-body"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={14}
                className="mt-1 w-full bg-transparent border border-[var(--ld-ink)]/25 px-3 py-2 text-[13.5px] leading-[1.65] text-[var(--ld-ink)] focus:outline-none focus:border-[var(--ld-gold-deep)] transition-colors font-serif-display"
              />
              <span className="block mt-1 text-[11px] text-[var(--ld-ink-muted)]">
                Blank line = new paragraph. Single newline = line break.
              </span>
            </label>
          </div>
        )}

        <div className="px-7 py-4 border-t border-[var(--ld-border)]/70 flex items-center justify-end gap-2">
          <button
            type="button"
            data-testid="welcome-email-cancel"
            onClick={onClose}
            className="h-10 px-5 text-[12px] tracking-[0.18em] uppercase border border-[var(--ld-ink)]/40 hover:border-[var(--ld-gold-deep)] hover:text-[var(--ld-gold-deep)] transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="welcome-email-send"
            onClick={send}
            disabled={loading || sending}
            className="inline-flex items-center gap-2 h-10 px-5 text-[12px] tracking-[0.18em] uppercase bg-[var(--ld-ink)] text-[var(--ld-ivory)] hover:bg-[var(--ld-gold-deep)] transition-colors disabled:opacity-60 disabled:cursor-wait"
          >
            <Send size={13} strokeWidth={1.8} />
            {sending ? "Sending…" : "Send welcome email"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WelcomeEmailModal;
